import {
  ensureSessionRefreshed,
  notifySessionActivity,
  SessionRefreshUnavailableError,
} from '@/auth/sessionTimer';
import { extractApiError } from '@/originator/statusUtils';

import { apiClient, idpCorrelationHeader } from './client';

/**
 * One answered field as BMO stores it. Structurally identical to the renderer's
 * `SnapshotFieldEntry` (`form-renderer/buildApplicationSnapshot.ts`) — declared here too so the
 * API layer doesn't depend on the renderer, and so a change to either side is a visible
 * contract change rather than a silent coupling.
 */
export type ApplicationFieldEntry = {
  label: string;
  value: unknown;
};

/**
 * The e-signature half of a save — what the browser resolved that the back end
 * cannot: the signer roster (gating conditions and repeatable-section instances
 * are evaluated client-side) and which files were uploaded (their metadata is
 * never persisted server-side).
 *
 * Everything else about `bmoData` — signer ids, ordering, application roles,
 * bindings, text anchors, delivery file names, document category — is composed
 * by the BFF from the workflow template. See `BmoDataBuilder`.
 */
export type ESignSubmission = {
  signers: Array<{
    /** A `SignatoryRole.slug` on the workflow's e-signature step. */
    roleSlug: string;
    /** 0-based repeatable-section instance, so one role can be several people. */
    instanceIndex: number;
    firstName: string;
    /** Empty when the config maps no last-name field. Never split off `firstName`. */
    lastName: string;
    email: string;
    phone: string;
    /**
     * What this signer's mapped language field answered, raw and un-normalized.
     * Absent when the role maps no language field (the norm) or the client has
     * not answered it — the BFF falls back to the application language for both,
     * and for a value it does not recognize.
     */
    language?: string;
  }>;
  docs: Array<{
    /** Identifies the file AND proves it is this advisor's. */
    s3Key: string;
    /** Delivered verbatim; for a bucket file its opening words are also the §14.1 DOCNAME. */
    description: string;
    /**
     * The originator entry this file fills; absent for the free-form bucket.
     *
     * Travels so the SAVE composes the identical §14.1 name the deliver call is about to send —
     * same inputs, same server-side resolver. BMO matches deliveries purely by name, so a save
     * that promised a different one would be a silent mismatch.
     */
    entryId?: string;
    /** 1-based review order within the package, assigned when the file was picked. */
    sequenceInPackage?: number;
  }>;
  /**
   * `StepFileEntry.id` of every PDF this application actually generates.
   *
   * Sent for the same reason the roster is: a PDF entry can be gated by
   * conditions that evaluate against values the server never receives, so only
   * the browser knows which documents exist. Without it, BMO is told about
   * signatures inside a document the delivery pipeline is never going to send.
   * The BFF checks each id against the template — this list can narrow what
   * BMO hears about, never widen it.
   */
  generatedPdfEntryIds: string[];
  /**
   * Every signature placement this application's answers gated out
   * (`SignatureField.conditions`).
   *
   * Same reasoning again, one level finer: an individual signature can be
   * conditioned out of a document that otherwise generates, and the conditions
   * evaluate against values the server never sees. Without this the BFF would
   * tell BMO to find an anchor the browser deliberately did not stamp into the
   * PDF — a package where OneSpan cannot locate a signature, which surfaces only
   * when somebody tries to sign it.
   *
   * An EXCLUSION list rather than a narrowing one like `generatedPdfEntryIds`:
   * absent means nothing was suppressed, which is exactly how every client built
   * before this member behaved, and "every signature conditioned out" stays
   * representable. It can only ever remove fields, so it carries the same trust
   * property — the BFF still checks each pair names a signature authored on this
   * workflow.
   */
  suppressedSignatures: Array<{
    /** `StepFileEntry.id` of the PDF the signature is placed in. */
    pdfEntryId: string;
    /** The signature's `textAnchor`, brackets included. */
    textAnchor: string;
  }>;
};

/** Everything needed to keep saving an application BMO has already created. */
export type ApplicationMetadata = {
  /** BMO's application id. Minted by BMO, never by us. */
  applicationId: string;
  templateId: string;
  /** The workflow's appName. */
  appType: string;
  /** The version label of the form whose CONTENT was filled — the branched-in workflow's
   *  when a form branch fired, the base form's otherwise. */
  templateVersion: string;
  /** BMO owns this value; every save echoes it back unchanged. */
  creationTimestamp: string;
  bmoStatus: string;
  /**
   * A form branch fired on this application: the content was filled against a different
   * workflow than `templateId` names.
   *
   * Which one is re-derived from the base template's declared rule, because the answer
   * that triggered the swap belongs to the base form and was never persisted. That is why
   * the convention is ONE branch rule per workflow — this boolean is all BMO records.
   *
   * Absent until BMO's retrieve contract carries it, which reads as "not branched"; a
   * branched application resumes as the base form until then.
   */
  subsequentApplication?: boolean;
};

export type ApplicationResume = {
  metadata: ApplicationMetadata;
  fieldValues: Record<string, ApplicationFieldEntry>;
  /**
   * Whether `fieldValues` IS the saved answers. Only `restored` means it is; every other value
   * means the map is an empty stand-in for answers BMO holds and we could not read (absent from
   * the response, or the PGP blob we sent handed back — ADR-0027). The runtime must not reopen
   * those as editable: saves carry whole state, so saving a form reopened blank overwrites them.
   */
  fieldValuesStatus: 'restored' | 'absent' | 'encrypted' | 'unreadable';
};

/**
 * The final-submit outcome. `delivered` is the only success — the application is at BMO AND its
 * package reached the FileX drop.
 *
 * `pending` is retained in the union but the BFF no longer returns it (2026-10-23). A failed
 * package delivery used to answer `202 pending`; it now answers `502 PACKAGE_DELIVERY_FAILED`,
 * because BMO does not act on an application until its package ZIP arrives — so "pending" named
 * a delivery that was not in flight and would never be retried. It stays in the type because the
 * FE and BFF deploy separately: during a rolling deploy a browser on the new bundle can still be
 * answered by the old API, and a `pending` that reaches `FormShell` is treated as success rather
 * than crashing on an unmodelled value.
 */
export type ApplicationSubmitResult = {
  applicationId: string;
  bmoStatus: string;
  deliveryStatus: 'delivered' | 'pending';
};

/**
 * Thrown when a save fails because the BFF session is gone. Callers must treat this as a
 * session-expiry event — the warning-modal / redirect-to-login flow — never as a generic
 * "save failed" toast: an advisor's in-progress snapshot should not appear lost to a silent
 * auth failure (ADR-0010 §Session expiry, ADR-0022).
 */
export class ApplicationSessionExpiredError extends Error {
  constructor() {
    super('The advisor session expired before the application could be saved.');
    this.name = 'ApplicationSessionExpiredError';
  }
}

/**
 * Advisor application runtime API. Every call here goes to BMO through the BFF — nothing is
 * stored on our side (spec §14).
 *
 * <h3>The session ping lives here, and nowhere else on this path</h3>
 * `request` awaits `ensureSessionRefreshed()` before issuing any call that spends the advisor's
 * ISAM access token, so the token is refreshed by the time the request goes out rather than
 * merely at the same time as it (ADR-0022). Callers must not ping the timer themselves: two
 * pings around one action are two concurrent `POST /auth/refresh` grants, ISAM rotates the
 * refresh token on use, and the loser sends one the winner has already consumed — `invalid_grant`,
 * which the BFF answers by ending the session and logging the advisor out mid-save.
 *
 * The two additional-documents calls opt out with `refreshFirst: false`. Neither resolves an
 * advisor token on the BFF (see ApplicationEndpoints.cs — one queues an SFTP delivery, the other
 * reads our own ledger), so a grant would buy them nothing, and `getDeliveryStatus` is a poll
 * that would spend one per tick.
 *
 * On a 401 every call re-pings regardless: that drives the timer's own refresh, which redirects
 * to login when the session really is gone.
 *
 * <h3>Which calls carry `idpCorrelationId`</h3>
 * Only `save` and `get` — the two that reach a BMO API. Their BFF routes drive
 * `GamxApplicationClient`, which is where the header is forwarded on (and which will own submit
 * when BMO supplies that contract, inheriting it for free).
 *
 * The rest deliberately omit it, because none of them produces a BMO REST call:
 * `deliverAdditionalDocuments` queues an SFTP drop to DC FileX, `getDeliveryStatus` reads our own
 * ledger, and `previewESignInfo` composes a payload on the BFF without sending it anywhere.
 */
export const applicationsApi = {
  /**
   * Pushes the whole application snapshot to BMO. Full state every time, never a delta
   * (ADR-0010 / A12).
   *
   * <h3>The first save is what creates the application</h3>
   * Omit `applicationId` and this posts to the collection, which sends BMO an empty `clientAppId`
   * and reads the id BMO mints off the response. Every save after that carries the id and goes to
   * the ID-scoped route. Nothing reaches BMO before that first save — an advisor who opens the
   * wrong workflow and backs out must not leave a record in BMO's database.
   *
   * Because the create call is just the first save, `idempotencyKey` matters most there: mint it
   * once per mounted application and reuse it, so a dropped response to the first save can't leave
   * BMO holding two applications for one click.
   *
   * <h3>Do not refresh the session around this call</h3>
   * `request` already awaits `ensureSessionRefreshed()` before the save leaves, so the BFF's stored
   * ISAM token is current by the time BMO sees it — that is this call's refresh, and it is the
   * only one it needs. A caller that pings as well turns one refresh into two concurrent
   * `refresh_token` grants; ISAM rotates on use, so the second is refused with `invalid_grant`
   * and the BFF ends the session, logging the advisor out on the very click that was trying to
   * save. `FormShell.persistSnapshot` did exactly that until 2026-08-26 — a save that succeeded
   * and a logout that landed 200 ms behind it.
   */
  async save(args: {
    /** Omitted until the first save has returned one — see above. */
    applicationId?: string;
    templateId: string;
    /**
     * The workflow a form branch has swapped in for the rest of this application, if any.
     *
     * `templateId` stays the BASE form throughout — it is what identifies the application,
     * and it is what `appType` / `smartforms.formId` / `smartforms.formName` are read from.
     * This names where the CONTENT came from, which is what the version label and the
     * PDF / e-signature / amount resolvers must be read from instead. Absent = an
     * unbranched application, exactly as before.
     */
    subsequentTemplateId?: string | null;
    language: 'en' | 'fr';
    /** Ignored on the create call — BMO stamps it. */
    creationTimestamp: string;
    fieldValues: Record<string, ApplicationFieldEntry>;
    /** The status this save transitions TO — where the advisor has reached on the workflow. */
    bmoStatus?: string;
    /**
     * The status this save transitions FROM: whatever BMO last confirmed. The BFF refuses the
     * save outright when it is one BMO drives, because editing out of those happens on their
     * side, not here. Omit when BMO has not confirmed anything yet.
     */
    knownBmoStatus?: string;
    /**
     * Signers + uploaded documents. Sent on every save with the shape it will always have,
     * carrying empty arrays until the advisor has resolved a roster or uploaded anything.
     *
     * What reaches BMO is composed by the BFF from this plus `bmoStatus`, and the two halves of
     * `bmoData` are gated separately (see `BmoDataBuilder.Build`). `applicantInfo` ships as soon as
     * a signatory has any details typed, so BMO's record tracks the advisor's answers. `eSignInfo`
     * waits until `bmoStatus` reaches ESIGNER-REVIEW-COMPLETE — i.e. the advisor has PASSED the
     * e-signature step — because before that the roster names people whose phone and email are
     * still empty, and BMO rejects the save over those missing values.
     *
     * Ignored outright on the create call, whatever it carries: supporting documents are named by
     * S3 keys minted from the application id, and the package documents by a §14.1 name built
     * around it, so neither can be named before BMO has answered with one. The next save carries
     * the roster in full — every save is full state, never a delta.
     */
    eSign: ESignSubmission;
    idempotencyKey: string;
    /**
     * The advisor's form-session id, constant across every save this sitting makes and forwarded
     * to BMO as `x-bmo-idp-correlation-id`. Minted once per mounted form by
     * `AdvisorApplicationPage`; optional here because preview and tests have no session to name.
     */
    idpCorrelationId?: string;
    /**
     * The instant of the press that triggered this save, minted ONCE by the caller and sent
     * unchanged to `deliverAdditionalDocuments` as well. It stamps every advisor upload's §14.1
     * name, and the deliver call that follows recomposes those same names — so a second read of
     * the clock there would promise BMO one document and deliver another.
     *
     * Only `FormShell.persistAndDeliver` sends it, because only it is followed by a deliver.
     * Everything else omits it and the server falls back to the application's creation instant.
     */
    deliveryTimestamp?: string;
  }): Promise<ApplicationMetadata> {
    // Both destructured out of the body: the id names the ROUTE, and the correlation id travels
    // as a header. Neither belongs in the snapshot BMO stores.
    const { applicationId, idpCorrelationId, ...body } = args;
    const path = applicationId
      ? `/applications/${encodeURIComponent(applicationId)}/save`
      : '/applications';
    return request(() =>
      apiClient.post<ApplicationMetadata>(path, body, {
        headers: idpCorrelationHeader(idpCorrelationId),
      }),
    );
  },

  /**
   * The advisor's final submit — the point-of-no-return call BMO's dedicated submit endpoint
   * handles, never `save` (the BFF refuses SUBMITTED there outright, BMO_STATUS_NOT_WRITABLE).
   *
   * Unlike `save`, this never carries a client-authored `bmoStatus` or a per-attempt idempotency
   * key: submit always transitions to exactly one status, and the BFF derives its own BMO
   * idempotency key from the application id alone.
   *
   * <h3>Safe to call again</h3>
   * A `PACKAGE_DELIVERY_FAILED` rejection — or any transient error after BMO has already accepted
   * the submission — means calling this again is not a resubmission: the BFF's split-brain marker
   * remembers BMO already said yes and only retries the DC FileX delivery. That is exactly what
   * makes it safe for the advisor to press Submit again after the support block, and it is the
   * fastest recovery this platform has for a stranded package.
   */
  async submit(args: {
    applicationId: string;
    templateId: string;
    /** See `save` — the branched-in workflow the content came from. Submit needs it for the
     *  same reason, and for one more: the delivered package is built from this template's
     *  approved version, so a submit that omitted it would ship the base form's PDFs. */
    subsequentTemplateId?: string | null;
    language: 'en' | 'fr';
    creationTimestamp: string;
    fieldValues: Record<string, ApplicationFieldEntry>;
    /**
     * The answer map the delivered package's PDFs are FILLED from — raw values, no labels,
     * pruned exactly as every preview and download prunes them (`pdfFillValues`).
     *
     * Deliberately not `fieldValues`. That one is the labelled snapshot BMO stores, and it is
     * built by a walk over authored FIELDS, so an answer no field holds is absent from it
     * unless something re-adds it by hand. A dual-unit repeatable column's "$ or %" mode is
     * exactly such an answer (`unitMode:<fieldId>:<columnId>`), and filling from the snapshot
     * printed percentages into the dollar boxes of a document a client signs — silently,
     * because the filler simply falls back to the column's authored default.
     *
     * Sending both means the wire carries the answers twice, which is the price of the
     * package and the record being the same thing by construction rather than by two walks
     * agreeing. Only this map reaches `PdfFunction`; only `fieldValues` reaches BMO.
     */
    pdfValues: Record<string, unknown>;
    /** See `save` — the status BMO last confirmed. Submit requires this at or past the rung this
     *  workflow's own milestone steps can award; the BFF is the authority on what that rung is. */
    knownBmoStatus?: string;
    eSign: ESignSubmission;
    /** Every additional-document S3 key this application's steps require, across the WHOLE
     *  template — the backstop re-check that catches a file that regressed after its own step
     *  already passed (PROJECT_PLAN.md). */
    additionalDocumentKeys: string[];
    idpCorrelationId?: string;
  }): Promise<ApplicationSubmitResult> {
    const { applicationId, idpCorrelationId, ...body } = args;
    return request(() =>
      apiClient.post<ApplicationSubmitResult>(
        `/applications/${encodeURIComponent(applicationId)}/submit`,
        body,
        { headers: idpCorrelationHeader(idpCorrelationId) },
      ),
    );
  },

  /**
   * Reads a saved application back so the advisor can resume it.
   *
   * BMO has not supplied a retrieve contract yet, so this currently answers 501 and the caller
   * is expected to fall back to an empty form. The call site stays put so the swap is
   * server-side only.
   */
  async get(
    applicationId: string,
    templateId: string,
    /** See `save` — the same form-session id, so a resume and the saves that follow it correlate
     *  to one sitting on BMO's side. */
    idpCorrelationId?: string,
  ): Promise<ApplicationResume> {
    return request(() =>
      apiClient.get<ApplicationResume>(`/applications/${encodeURIComponent(applicationId)}`, {
        params: { templateId },
        headers: idpCorrelationHeader(idpCorrelationId),
      }),
    );
  },

  /**
   * Asks the BFF to deliver a set of ready additional files to BMO's DC FileX drop (ADR-0026).
   *
   * Answers `202` and returns immediately — the work runs behind a queue, and
   * `getDeliveryStatus` reports when it lands. Files already delivered are skipped server-side,
   * so calling this again after a Back-and-forward is safe and enqueues nothing.
   *
   * MUST be called only after the save succeeded. Spec §14 fixes that order: "BMO acts on the
   * submitted documents we SFTP to them, not on the JSON save data we send via the API", so the
   * save establishes the record the documents attach to.
   */
  async deliverAdditionalDocuments(args: {
    applicationId: string;
    templateId: string;
    /**
     * The branched-in workflow, checked server-side exactly as `save`'s is.
     *
     * `templateId` stays the BASE form here too — it is what every file key's ownership is
     * verified against, and what §14.1's APPTYPE field carries. This names the template each
     * file's `entryId` is resolved against, because the branch target owns the
     * additional-documents step and every entry on it.
     */
    subsequentTemplateId?: string | null;
    /**
     * Each uploaded file with the description that names it. This call is where §14.1's DOCNAME
     * is decided — the object in S3 carries the advisor's own filename — so the descriptions have
     * to travel with it rather than having been captured at upload.
     */
    files: Array<{
      key: string;
      entryId?: string;
      description?: string;
      sequenceInPackage?: number;
    }>;
    /** See the server's `DeliverAdditionalDocumentsRequest.CreationTimestamp`. */
    creationTimestamp?: string;
    /**
     * The press instant, the SAME value the save that just ran carried. See the server's
     * `DeliverAdditionalDocumentsRequest.DeliveryTimestamp` — sharing it is what makes the names
     * this call composes identical to the ones that save already promised BMO.
     */
    deliveryTimestamp?: string;
  }): Promise<{ enqueued: number }> {
    return request(
      () =>
        apiClient.post<{ enqueued: number }>(
          `/applications/${encodeURIComponent(args.applicationId)}/additional-documents/deliver`,
          {
            templateId: args.templateId,
            subsequentTemplateId: args.subsequentTemplateId ?? undefined,
            files: args.files,
            creationTimestamp: args.creationTimestamp,
            deliveryTimestamp: args.deliveryTimestamp,
          },
        ),
      // Queues an SFTP delivery; the BFF resolves no advisor token for it, so a refresh grant
      // would buy nothing.
      { refreshFirst: false },
    );
  },

  /**
   * Batched delivery-status poll.
   *
   * A key that is not this advisor's — or has no ledger row — answers `missing` rather than an
   * error, deliberately: the caller learns nothing from the refusal.
   */
  async getDeliveryStatus(args: {
    applicationId: string;
    templateId: string;
    keys: string[];
  }): Promise<AdditionalDocumentDeliveryStatus[]> {
    const { results } = await request(
      () =>
        apiClient.post<{ results: AdditionalDocumentDeliveryStatus[] }>(
          `/applications/${encodeURIComponent(args.applicationId)}/additional-documents/delivery-status`,
          { templateId: args.templateId, keys: args.keys },
        ),
      // Reads our own delivery ledger, and it is a poll: refreshing here would spend one ISAM
      // grant per tick for a call that never touches the token.
      { refreshFirst: false },
    );

    // Same CloudFront SPA-fallback trap the other polls guard against: a 404 from the API origin
    // comes back as 200 + index.html, so `results` reads `undefined`. Throwing routes it to the
    // caller's transient-error path rather than reading as "nothing delivered".
    if (!Array.isArray(results)) {
      throw new Error('Delivery-status request did not return results.');
    }
    return results;
  },
};

/**
 * One file's delivery state.
 *
 * `status` is deliberately a plain string rather than a union: an older bundle must pass through
 * a value it does not recognise rather than crashing on it.
 */
export type AdditionalDocumentDeliveryStatus = {
  key: string;
  /** `pending` | `delivered` | `failed` | `missing`. */
  status: string;
  /** Machine-readable reason on a failure. For support, not for rendering. */
  failureCode?: string | null;
};

/**
 * What the dev-only eSignInfo probe answers with.
 *
 * `bmoData` is `null` only until the roster produces something worth sending; from there the
 * builder sends it on every save, partially populated. The three reason strings cover the states an
 * operator would otherwise have to guess at — an empty panel gives no way to tell "not yet" from
 * "broken".
 */
export type ESignInfoProbeResult = {
  /** BMO's id, or the server's `DEV-PREVIEW` marker — see {@link isPreviewApplicationId}. */
  applicationId: string;
  /** True when no real application exists, so the §14.1 delivery names below are illustrative. */
  isPreviewApplicationId: boolean;
  fileXEnvironment: string;
  /** `saveRequest.bmoData` exactly as composed, or null. Left untyped: the value of a diagnostic
   *  is showing what the server actually produced, and a mirrored type here could only narrow it. */
  bmoData: unknown | null;
  /** Why `bmoData` is null; null when it was sent. */
  omittedReason: string | null;
  /** Why a payload that WAS sent carries no `eSignInfo`; null when it carries one. */
  eSignInfoOmittedReason: string | null;
  /** Why an `eSignInfo` that WAS sent carries `documents: []`; null when it carries some. */
  emptyDocumentsReason: string | null;
};

/**
 * TODO(BMO-PRELAUNCH-DIAGNOSTICS): remove with the endpoint before go-live.
 *
 * Asks the BFF to compose `saveRequest.bmoData` for the application in progress and hand it back
 * instead of sending it. Nothing is written anywhere — no BMO call, no S3, no ledger row.
 *
 * <h3>Why the server rather than the browser</h3>
 * Signer ids, ordering, `{signer.anchor}` bindings, date fields and §14.1 delivery names are all
 * composed by `BmoDataBuilder` and never cross to the client. Rebuilding them here to avoid a round
 * trip would create a second source of truth that drifts — and a probe that shows a payload BMO
 * never receives is worse than no probe.
 *
 * Deliberately NOT on `apiClient`'s `/api/v1` base: this is a diagnostics route, mapped only where
 * `BmoDiagnostics.IsEnabled` holds, so a 404 here in an environment that shouldn't have it is the
 * correct and expected answer.
 */
export async function previewESignInfo(args: {
  templateId: string;
  /**
   * The workflow a form branch has swapped in, exactly as `save` takes it — and sent for the
   * same reason. `templateId` stays the BASE form; this is what the server composes the
   * e-signature block against, so a probe that omitted it would describe the base form's
   * documents and placements rather than the ones the advisor is about to sign.
   */
  subsequentTemplateId?: string | null;
  language: 'en' | 'fr';
  /** Omitted in preview, and in the advisor runtime before the first save has returned one. */
  applicationId?: string;
  /** BMO's creation timestamp — the fallback stamp for §14.1 names. */
  creationTimestamp?: string;
  /**
   * The press instant a real save would carry, which is what SUPPORTING document names are
   * stamped with. Without it the probe shows the fallback stamp rather than what the next save
   * actually sends.
   */
  deliveryTimestamp?: string;
  /** Where the runtime sits on the ladder — it decides whether `eSignInfo` is composed at all. */
  bmoStatus?: string;
  eSign: ESignSubmission;
}): Promise<ESignInfoProbeResult> {
  return request(
    () =>
      apiClient.post<ESignInfoProbeResult>(
        '/test/bmo/esign-info',
        {
          templateId: args.templateId,
          subsequentTemplateId: args.subsequentTemplateId ?? undefined,
          language: args.language,
          applicationId: args.applicationId,
          creationTimestamp: args.creationTimestamp,
          deliveryTimestamp: args.deliveryTimestamp,
          bmoStatus: args.bmoStatus,
          eSign: args.eSign,
        },
        // apiClient's base is `/api/v1`; the diagnostics group is mounted at `/api/test`.
        { baseURL: '/api' },
      ),
    // Pure composition on the BFF — it resolves no advisor token, so a refresh grant would buy
    // nothing and would spend one ISAM rotation per button press.
    { refreshFirst: false },
  );
}

/** True when the error is the BFF reporting that resume isn't wired up at BMO yet. */
export function isResumeUnavailable(err: unknown): boolean {
  const { status, code } = extractApiError(err) as { status?: number; code?: string };
  return status === 501 || code === 'BMO_CONTRACT_UNAVAILABLE';
}

async function request<T>(
  call: () => Promise<{ data: T }>,
  /** `refreshFirst: false` for calls the BFF serves without an advisor token — see the module comment. */
  opts?: { refreshFirst?: boolean },
): Promise<T> {
  if (opts?.refreshFirst !== false) {
    try {
      await ensureSessionRefreshed();
    } catch (err) {
      // The token behind this call was already spent and the refresh meant to replace it did
      // not land. The session is NOT known to be gone — a blip at the IdP looks exactly like
      // this — so no redirect is in flight and the session-expiry path would be a lie. What
      // the call must not do is leave on a token we know is dead: BMO answers that with a 401
      // the advisor only ever sees as an unexplained failure. Rethrown as itself so the caller
      // shows its ordinary, retryable save-failure instead.
      if (err instanceof SessionRefreshUnavailableError) throw err;
      // The timer confirmed the session is gone and has already started the redirect. Sending
      // the call now could only 401 in turn, so it never leaves — and the caller takes its
      // session-expiry path rather than showing a "save failed" toast over the redirect.
      throw new ApplicationSessionExpiredError();
    }
  }
  try {
    const { data } = await call();
    return data;
  } catch (err) {
    if (extractApiError(err).status === 401) {
      // Wake the session timer: its refresh will confirm the session is gone and run the
      // redirect-to-login flow. Then signal the caller to skip its error toast. Fire-and-forget
      // rather than awaited — the error below is the answer either way.
      notifySessionActivity();
      throw new ApplicationSessionExpiredError();
    }
    throw err;
  }
}
